import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

// Read cached tier synchronously so the PortalShell can render the correct
// pill on the very first paint after a tab switch — avoids the annoying
// "Creator → Free → Creator" flicker that used to happen when each page
// re-mounted PortalShell and re-fetched /subscriptions/me.
const readCachedTier = (uid) => {
  if (!uid) return "";
  try { return localStorage.getItem(`tier:${uid}`) || ""; } catch { return ""; }
};
const writeCachedTier = (uid, t) => {
  if (!uid || !t) return;
  try { localStorage.setItem(`tier:${uid}`, t); } catch {}
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Whenever we have a client-side user, hydrate `tier` from cache first
  // (so the pill renders instantly), then refresh from the server once.
  // We deliberately only refetch when the user id changes — not on every
  // tab click — so the value stays stable and the pill never flickers.
  useEffect(() => {
    if (!user?.id) { setTier(""); return; }
    // Shooters don't have a subscription — skip the fetch entirely.
    if (user.role === "photographer" || user.role === "videographer") {
      setTier("");
      return;
    }
    const cached = readCachedTier(user.id);
    if (cached) setTier(cached);
    api.get("/subscriptions/me").then((r) => {
      const t = r.data?.tier || "free";
      setTier(t);
      writeCachedTier(user.id, t);
    }).catch(() => {});
  }, [user?.id, user?.role]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    // Clear cached tier so the next user doesn't inherit it.
    try { if (user?.id) localStorage.removeItem(`tier:${user.id}`); } catch {}
    setUser(null);
    setTier("");
  };

  return (
    <AuthCtx.Provider value={{ user, loading, tier, login, signup, logout, refresh, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
